import { useCallback, useMemo } from "react";
import {
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
    type ColumnDef,
} from "@tanstack/react-table";
import type { TaskWithOccurrence } from "@dashboard/utils/taskWithOccurrence";
import { OCCURRENCE_STATUS_CONFIG } from "@dashboard/utils/occurrenceStatusConfig";
import { useTranslation } from "react-i18next";
import { useAbility } from "@casl/react";
import { TaskAbilityContext } from "@dashboard/casl/taskAbility";
import { formatDateShort } from "@dashboard/utils/formatDate";

export type AnnualCycleFilters = {
    selectedMonths: number[];
    status: string[];
    responsible: string[];
    search: string;
    year: number;
};

export function useAnnualCycleTable(
    data: TaskWithOccurrence[],
    columns: ColumnDef<TaskWithOccurrence>[],
    filters: AnnualCycleFilters,
) {
    const { t, i18n } = useTranslation();
    const ability = useAbility(TaskAbilityContext);

    const getSearchableStatusValues = useCallback(
        (status: string): string[] => {
            const config =
                OCCURRENCE_STATUS_CONFIG[status as keyof typeof OCCURRENCE_STATUS_CONFIG];
            const translatedValue = config ? t(config.labelKey) : status;
            return [status, translatedValue].filter(Boolean);
        },
        [t],
    );

    const columnFilters = useMemo(() => {
        const cf: { id: string; value: unknown }[] = [];

        if (filters.selectedMonths.length > 0) {
            cf.push({
                id: "dueDate",
                value: { months: filters.selectedMonths, year: filters.year },
            });
        } else if (filters.year) {
            cf.push({ id: "dueDate", value: { months: [], year: filters.year } });
        }
        return cf;
    }, [filters.selectedMonths, filters.year]);

    const table = useReactTable<TaskWithOccurrence>({
        data,
        columns,
        state: {
            columnFilters,
            globalFilter: filters.search ?? "",
            columnVisibility: {
                delete: ability.can("delete", "Task"),
            },
        },
        globalFilterFn: (row, _columnId, filterValue) => {
            const searchTerm = String(filterValue).toLowerCase();
            if (!searchTerm) return true;

            const task = row.original;
            const taskTitle = (task.title || "").toLowerCase();
            const responsible = task.ownerUser
                ? `${task.ownerUser.firstName} ${task.ownerUser.lastName}`.toLowerCase()
                : "";
            const taskDate = task.dueDate ? formatDateShort(task.dueDate, i18n.language) : "";
            const linkTitles = (task.links ?? []).map((link) => (link.title ?? "").toLowerCase());
            const occurrenceStatus = task.occurrence?.status ?? "OPEN";
            const statusValues = getSearchableStatusValues(occurrenceStatus);
            const statusSearchTerms = statusValues.map((val) => val.toLowerCase());

            const searchableFields = [
                taskTitle,
                responsible,
                taskDate,
                ...linkTitles,
                ...statusSearchTerms,
            ];
            return searchableFields.some((field) => field.includes(searchTerm));
        },
        initialState: {
            pagination: {
                pageSize: 15,
                pageIndex: 0,
            },
            sorting: [{ id: "dueDate", desc: true }],
        },
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    return table;
}
